import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { CalendarMonth, ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useScheduledPlmResponsesForProperty } from '../../../system/EntityManager';

interface CalendarDay {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  hasEvents: boolean;
  events: Array<{
    id: string;
    start: Date;
    end: Date;
    title: string;
  }>;
}

export function PlmCalendar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('propertyId');
  const unitId = searchParams.get('unitId');
  const returnTo = searchParams.get('returnTo') || '/properties';

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [showEventDialog, setShowEventDialog] = useState(false);

  const { scheduledResponses, currentResponses, loading } =
    useScheduledPlmResponsesForProperty(propertyId || '');

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: CalendarDay[] = [];
    const currentDateForLoop = new Date(startDate);

    while (currentDateForLoop <= lastDay || days.length < 42) {
      const dayDate = new Date(currentDateForLoop);
      const isCurrentMonth = dayDate.getMonth() === month;

      // Find events for this day
      const dayEvents = [...scheduledResponses, ...currentResponses]
        .filter((response) => {
          const eventDate = new Date(response.scheduledStart);
          return eventDate.toDateString() === dayDate.toDateString();
        })
        .map((response) => ({
          id: response._id,
          start: new Date(response.scheduledStart),
          end: new Date(response.scheduledEnd),
          title: `PLM Event - ${response.event?._id || 'Unknown Event'}`,
        }));

      // If unitId is provided, filter events to only include those with curtailment tasks for this unit
      let filteredDayEvents = dayEvents;
      if (unitId) {
        // This would need to be enhanced to actually filter by unit-specific tasks
        // For now, we'll show all events but this could be enhanced with additional data
        filteredDayEvents = dayEvents;
      }

      days.push({
        date: dayDate,
        day: dayDate.getDate(),
        isCurrentMonth,
        hasEvents: filteredDayEvents.length > 0,
        events: filteredDayEvents,
      });

      currentDateForLoop.setDate(currentDateForLoop.getDate() + 1);
    }

    return days;
  }, [currentDate, scheduledResponses, currentResponses, unitId]);

  const monthName = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const handlePreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const handleDayClick = (day: CalendarDay) => {
    if (day.hasEvents) {
      setSelectedDay(day);
      setShowEventDialog(true);
    }
  };

  const handleEventClick = (eventId: string) => {
    setShowEventDialog(false);
    navigate(
      `/plm/event-responses/${eventId}/timeline?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`,
    );
  };

  const handleCloseDialog = () => {
    setShowEventDialog(false);
    setSelectedDay(null);
  };

  const handleBackToProperties = () => {
    navigate(returnTo);
  };

  if (!propertyId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" color="error">
          Property ID is required
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={handleBackToProperties}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          PLM Event Calendar
        </Typography>
      </Box>

      {/* Calendar Navigation */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <IconButton onClick={handlePreviousMonth}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">{monthName}</Typography>
          <IconButton onClick={handleNextMonth}>
            <ArrowForward />
          </IconButton>
        </Box>
      </Paper>

      {/* Calendar Grid */}
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={1}>
          {/* Day Headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Grid item xs key={day}>
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {day}
                </Typography>
              </Box>
            </Grid>
          ))}

          {/* Calendar Days */}
          {calendarDays.map((day, index) => (
            <Grid item xs key={index}>
              <Box
                sx={{
                  p: 1,
                  height: 80,
                  border: '1px solid #e0e0e0',
                  cursor: day.hasEvents ? 'pointer' : 'default',
                  backgroundColor: day.isCurrentMonth ? 'white' : '#f5f5f5',
                  '&:hover': day.hasEvents
                    ? {
                        backgroundColor: '#e3f2fd',
                        borderColor: '#2196f3',
                      }
                    : {},
                }}
                onClick={() => handleDayClick(day)}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: day.isCurrentMonth
                      ? 'text.primary'
                      : 'text.disabled',
                    fontWeight: day.hasEvents ? 'bold' : 'normal',
                  }}
                >
                  {day.day}
                </Typography>
                {day.hasEvents && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={`${day.events.length} event${day.events.length > 1 ? 's' : ''}`}
                      size="small"
                      color={
                        day.events.some((e) =>
                          currentResponses.some((cr) => cr._id === e.id),
                        )
                          ? 'success'
                          : 'primary'
                      }
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Event Details Dialog */}
      <Dialog
        open={showEventDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Events for{' '}
          {selectedDay?.date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </DialogTitle>
        <DialogContent>
          {selectedDay?.events.map((event, index) => (
            <ListItem
              key={event.id}
              button
              onClick={() => handleEventClick(event.id)}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <ListItemText
                primary={event.title}
                secondary={`${event.start.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })} - ${event.end.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}`}
              />
            </ListItem>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
